import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { type UUID } from '~/types/common';

import { queryKeysInvoiceReprocessing } from '../queryKeys';

import { fetchReprocessingInvoice } from './fetchReprocessingInvoice';
import { type ReprocessingInvoiceResponse } from './types';

/**
 * React Query based custom hook for getting the data for an invoice requiring reprocessing with the given process ID.
 * @param processId - The ID of the process to fetch the invoice for.
 * @param options - Optional configuration for the query.
 * @returns The query result containing the reprocessing invoice data.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Invoice/operation/get_process_details_invoice_process__process_id__get
 */
export const useQueryReprocessingInvoice = (
  processId: UUID,
  options?: Omit<
    UseQueryOptions<ReprocessingInvoiceResponse>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery<ReprocessingInvoiceResponse>({
    enabled: Boolean(processId),
    queryFn: async () => fetchReprocessingInvoice({ processId }),
    queryKey: queryKeysInvoiceReprocessing.get(processId),
    ...options,
  });
};
